import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Image,
} from "@chakra-ui/react";
import imageA from "../../assets/images/co2_1/A.png";
import imageB from "../../assets/images/co2_1/B.png";
import imageC from "../../assets/images/co2_1/C.png";

type HistoryRecord = {
  date: string;
  score: number;
  evaluation: "A" | "B" | "C";
  threeMonthScore?: number;
  currentScore?: number;
};

type Co21HistoryTableProps = {
  records: HistoryRecord[];
};

const TABLE_STYLES = {
  th: {
    base: {
      fontSize: "lm" as const,
      borderWidth: "1px",
      paddingY: 2,
      textTransform: "none" as const,
    },
    center: {
      textAlign: "center" as const,
      style: { paddingInlineStart: 0, paddingInlineEnd: 0 },
    },
    left: {
      textAlign: "left" as const,
      paddingLeft: 1,
    },
  },
  td: {
    base: {
      borderWidth: "1px",
      paddingY: 1,
      fontSize: "lm" as const,
    },
    center: {
      textAlign: "center" as const,
      style: { paddingInlineStart: 0, paddingInlineEnd: 0 },
    },
    left: {
      textAlign: "left" as const,
      paddingLeft: 1,
    },
  },
};

export function Co21HistoryTable({
  records,
}: Readonly<Co21HistoryTableProps>): ReactElement {
  const { t } = useTranslation();

  const getEvaluationImage = (evaluation: "A" | "B" | "C"): string => {
    const imageConfig = {
      A: imageA,
      B: imageB,
      C: imageC,
    };
    return imageConfig[evaluation];
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${month}/${day} ${hours}:${minutes}`;
  };

  const tableHistoryColumns = [
    {
      width: "30%",
      title: t("PJ.Co21Result.MeasurementDate"),
      subTitle: undefined,
      align: "center" as const,
    },
    {
      width: "40%",
      title: t("PJ.Co21Result.ThreeMonthScore"),
      subTitle: t("PJ.Co21Result.ThreeMonthAverage"),
      align: "center" as const,
    },
    {
      width: "30%",
      title: t("PJ.Co21Result.Evaluation"),
      subTitle: t("PJ.Co21Result.ThreeMonthAverage"),
      align: "center" as const,
    },
  ];

  if (records.length === 0) {
    return <></>;
  }

  return (
    <Flex flexDirection="column" mt="0px">
      <Text mb="5px" color="#555555">
        {t("PJ.Co21Result.ThreeMonthHistory")}
      </Text>
      <Box
        maxHeight="400px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="#CCCCCC"
        borderTop="1px solid #CCCCCC"
        borderLeft="1px solid #CCCCCC"
      >
        <Table
          style={{ tableLayout: "fixed", borderSpacing: 0 }}
          bg="common.base"
          fontSize="sm"
          sx={{
            borderCollapse: "separate",
            th: {
              borderColor: "#CCCCCC",
              borderRight: "1px solid #CCCCCC",
              borderBottom: "1px solid #CCCCCC",
              borderLeft: "none",
              borderTop: "none",
            },
            td: {
              borderColor: "#CCCCCC",
              borderRight: "none",
              borderBottom: "1px solid #CCCCCC",
              borderLeft: "none",
              borderTop: "none",
            },
          }}
        >
          <Thead position="sticky" top="0" zIndex="1" bg="#F5F5F5">
            <Tr>
              {tableHistoryColumns.map((value, index) => (
                <Th
                  key={index}
                  width={{ base: value.width, sm: value.width }}
                  {...TABLE_STYLES.th.base}
                  {...TABLE_STYLES.th.center}
                  bg="#F5F5F5"
                  borderWidth="0"
                  borderRight="1px solid #CCCCCC"
                  borderBottom="1px solid #CCCCCC"
                >
                  {value.title}{" "}
                  {value.subTitle && (
                    <Box>
                      <Text fontSize="10px">{value.subTitle}</Text>
                    </Box>
                  )}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {records.map((record, index) => (
              <Tr key={index}>
                <Td {...TABLE_STYLES.td.base} {...TABLE_STYLES.td.center}>
                  {formatDateTime(record.date)}
                </Td>
                <Td {...TABLE_STYLES.td.base} {...TABLE_STYLES.td.center}>
                  {record.threeMonthScore?.toFixed(1) ?? "XX.X"}
                </Td>
                <Td {...TABLE_STYLES.td.base} {...TABLE_STYLES.td.center} p={1}>
                  {record.evaluation ? (
                    <Image
                      src={getEvaluationImage(record.evaluation)}
                      alt={record.evaluation}
                      width="32px"
                      height="32px"
                      margin="auto"
                    />
                  ) : (
                    "-"
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Flex>
  );
}
